import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, TransitionGroup as _TransitionGroup, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed bottom-4 right-4 z-[90] flex flex-col gap-2 w-72"
}
const _hoisted_2 = { class: "flex-shrink-0 h-8 w-8 rounded bg-muted flex items-center justify-center" }
const _hoisted_3 = { class: "flex-1 min-w-0" }
const _hoisted_4 = { class: "text-sm font-medium truncate" }
const _hoisted_5 = { class: "flex items-center gap-1 flex-shrink-0" }

import { ref, onMounted } from 'vue'
import { useMinimizedDocuments, type MinimizedDocument, type MinimizedDocumentForm } from '@/composables/useMinimizedDocuments'
import type { Document, DocumentType } from '@/types'
import DocumentFormDialog from '@/views/Documents/components/DocumentFormDialog.vue'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FileText, Maximize2, X } from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'MinimizedDocumentsWidget',
  setup(__props) {

const { minimizedDocuments, restore, discard, minimize, activeDocument, clearActiveDocument } = useMinimizedDocuments()

// State for restored dialog
const showRestoredDialog = ref(false)
const restoredItem = ref<MinimizedDocument | null>(null)
const restoredFormState = ref<{ title: string; type: DocumentType; content: string; isPublic: boolean } | null>(null)

const handleRestore = (id: string) => {
  const item = restore(id)
  if (!item) return
  restoredItem.value = item
  restoredFormState.value = { ...item.form }
  showRestoredDialog.value = true
}

const handleDiscard = (id: string) => {
  discard(id)
}

const handleRestoredSuccess = () => {
  showRestoredDialog.value = false
  restoredItem.value = null
  restoredFormState.value = null
}

const handleRestoredMinimize = (formState: MinimizedDocumentForm) => {
  if (!restoredItem.value) return
  minimize({
    form: formState,
    entityType: restoredItem.value.entityType,
    entityId: restoredItem.value.entityId,
    document: restoredItem.value.document,
  })
  showRestoredDialog.value = false
  restoredItem.value = null
  restoredFormState.value = null
}

const handleRestoredClose = (open: boolean) => {
  if (!open) {
    showRestoredDialog.value = false
    restoredItem.value = null
    restoredFormState.value = null
  }
}

const getDisplayTitle = (form: MinimizedDocumentForm) => {
  return form.title.trim() || 'Nuevo Documento'
}

// Auto-recover document that was open when page reloaded — add as minimized
onMounted(() => {
  const active = activeDocument.value
  if (active) {
    clearActiveDocument()

    // Avoid duplicates: check if this document is already minimized
    const alreadyMinimized = active.documentId
      ? minimizedDocuments.value.some((d) => d.document?.id === active.documentId)
      : false

    if (!alreadyMinimized) {
      minimize({
        form: { ...active.form },
        entityType: active.entityType,
        entityId: active.entityId,
        document: active.documentId ? { id: active.documentId } as Document : null,
      })
    }
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(minimizedDocuments).length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_TransitionGroup, {
            "enter-active-class": "transition-all duration-300 ease-out",
            "leave-active-class": "transition-all duration-200 ease-in",
            "enter-from-class": "opacity-0 translate-y-4 scale-95",
            "enter-to-class": "opacity-100 translate-y-0 scale-100",
            "leave-from-class": "opacity-100 translate-y-0 scale-100",
            "leave-to-class": "opacity-0 translate-y-4 scale-95"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(minimizedDocuments), (item) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: item.id,
                  class: "bg-background border rounded-lg shadow-lg p-3 flex items-center gap-3"
                }, [
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(_unref(FileText), { class: "h-4 w-4 text-muted-foreground" })
                  ]),
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("p", _hoisted_4, _toDisplayString(getDisplayTitle(item.form)), 1),
                    _createVNode(_unref(Badge), {
                      variant: "secondary",
                      class: "text-xs mt-0.5"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(item.document ? 'Editando' : 'Nuevo'), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_unref(Button), {
                      variant: "ghost",
                      size: "icon",
                      class: "h-7 w-7",
                      onClick: ($event: any) => (handleRestore(item.id)),
                      title: "Restaurar"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Maximize2), { class: "h-3.5 w-3.5" })
                      ]),
                      _: 1
                    }, 8, ["onClick"]),
                    _createVNode(_unref(Button), {
                      variant: "ghost",
                      size: "icon",
                      class: "h-7 w-7 text-muted-foreground hover:text-destructive",
                      onClick: ($event: any) => (handleDiscard(item.id)),
                      title: "Descartar"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(X), { class: "h-3.5 w-3.5" })
                      ]),
                      _: 1
                    }, 8, ["onClick"])
                  ])
                ]))
              }), 128))
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    (restoredItem.value)
      ? (_openBlock(), _createBlock(DocumentFormDialog, {
          key: 1,
          open: showRestoredDialog.value,
          "entity-type": restoredItem.value.entityType,
          "entity-id": restoredItem.value.entityId,
          document: restoredItem.value.document,
          "initial-form-state": restoredFormState.value,
          "onUpdate:open": handleRestoredClose,
          onSuccess: handleRestoredSuccess,
          onMinimize: handleRestoredMinimize
        }, null, 8, ["open", "entity-type", "entity-id", "document", "initial-form-state"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})